import api from './axios';

const persistAuthPayload = (data) => {
  if (!data?.accessToken) return;
  localStorage.setItem('token', data.accessToken);
  localStorage.setItem('refreshToken', data.refreshToken || '');
  localStorage.setItem('user', JSON.stringify(data.user));
};

export const authAPI = {
  // Register
  checkAvailability: async ({ email, phone } = {}) => {
    const params = new URLSearchParams();
    if (email) params.append('email', email);
    if (phone) params.append('phone', phone);
    const response = await api.get(`/auth/check-availability?${params.toString()}`, { silent: true });
    return response.data; // { email?: { available }, phone?: { available } }
  },

  register: async (data) => {
    const response = await api.post('/auth/register', data, { silent: true });
    return response.data;
  },

  // Login
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    persistAuthPayload(response.data);
    return response.data;
  },

  // Google sign-in — silent: GoogleSignInButton shows its own error toast
  googleLogin: async (credential) => {
    const response = await api.post('/auth/google', { credential }, { silent: true });
    persistAuthPayload(response.data);
    return response.data;
  },

  // Google onboarding completion
  completeGoogleOnboarding: async (data) => {
    const response = await api.post('/auth/google/onboarding', data);
    persistAuthPayload(response.data);
    return response.data;
  },

  // Logout
  logout: async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    
    // Try to invalidate refresh token on backend
    if (refreshToken) {
      try {
        await api.post('/auth/logout', { refreshToken });
      } catch (error) {
        // Even if backend call fails, continue with local logout
        console.error('Logout: Failed to invalidate token on server', error);
      }
    }
    
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },

  // Get current user
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Update profile
  updateProfile: async (data) => {
    const response = await api.patch('/auth/me', data);
    return response.data;
  },

  // Get user from localStorage
  getLocalUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Check if authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // Get refresh token
  getRefreshToken: () => {
    return localStorage.getItem('refreshToken');
  },

  // Set new tokens (after refresh)
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('token', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  },

  // Real platform user counts (admin only)
  adminStats: async () => {
    const response = await api.get('/auth/admin/stats', { silent: true });
    return response.data; // { totalUsers, clients, providers, newThisMonth, monthlySignups }
  },

  // Set first password on a Google-only account (no current password exists)
  setPassword: async (newPassword) => {
    const response = await api.post('/auth/me/set-password', { newPassword }, { silent: true });
    return response.data;
  },

  // Reset password (when passwordResetRequired is true)
  resetPassword: async (currentPassword, newPassword) => {
    const response = await api.patch('/auth/me/password', { currentPassword, newPassword });
    if (response.data.accessToken) {
      localStorage.setItem('token', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken || '');
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
};
